import React, { useState } from "react";
import { Box, Button, Paper, Stack, Typography, useMediaQuery, useTheme } from "@mui/material";
import { DeleteForever } from "@mui/icons-material";
import EditIcon from '@mui/icons-material/Edit';

import Chip from "components/uiElements/dataDisplay/Chip";
import { Tag } from "./TagManagementPage";
import TagQueriesDialog from "./TagQueriesDialog";
import TagDashboardsDialog from "./TagDashboardsDialog";
import { useTranslation } from "react-i18next";

interface TagManagementRowProps {
  onClick(): void;
  onDelete(): void;
  tag: Tag;
}

export default function TagManagementRow(props: TagManagementRowProps): React.JSX.Element {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const { i18n } = useTranslation();

  const [queryCount, setQueryCount] = useState<number>(props.tag.query_count ?? 0);
  const [dashboardCount, setDashboardCount] = useState<number>(props.tag.dashboard_count ?? 0);
  const [showTagQueryUsage, setShowTagQueryUsage] = useState<boolean>(false);
  const [showTagDashboardUsage, setShowTagDashboardUsage] = useState<boolean>(false);

  const gridTemplateAreas = !isSmallScreen ? `"tag description description usage"`
    : `"tag tag usage usage" "description description description description"`;

  return (
    <Paper>
      <Stack
        direction="row"
        onClick={() => {
          props.onClick();
          }}
        sx={{
          paddingTop: 0.5,
          paddingRight: 1,
          paddingBottom: 0.5,
          paddingLeft: 1,
          cursor: "pointer",
          "&:hover": { backgroundColor: theme.palette.action.hover }
        }}>
        <Box
          sx={{
            columnGap: 1,
            display: "grid",
            flex: 1,
            gridTemplateColumns: "repeat(4, 1fr)",
            gridTemplateRows: "auto",
            gridTemplateAreas: gridTemplateAreas,
            rowGap: 0,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gridArea: "tag"
            }}>
            <Chip color={props.tag.color} label={props.tag.name} />
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              paddingLeft: 1,
              gridArea: "description"
            }}>
            {props.tag.description}
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "start",
              gridArea: "usage"
            }}>
            <Button 
              disabled={!props.tag.dashboard_count}
              onClick={(event) => {
                event.stopPropagation();
                setShowTagDashboardUsage(true);
              }}
              size="small" 
              sx={{visibility: "hidden", opacity: !!props.tag.dashboard_count ? 1 : 0.5}}
            >
              <Stack>
                <Typography variant="body2">{i18n.t("tag-management-row.heading-dashboards")}</Typography>
                <Typography variant="h4">{dashboardCount}</Typography>
              </Stack>
            </Button>
            <Button 
              disabled={!props.tag.query_count}
              onClick={(event) => {
                event.stopPropagation();
                setShowTagQueryUsage(true);
              }}
              size="small"
              sx={{opacity: !!props.tag.query_count ? 1 : 0.5}}
            >
              <Stack>
                <Typography variant="body2">{i18n.t("tag-management-row.heading-queries")}</Typography>
                <Typography variant="h4">{queryCount}</Typography>
              </Stack>
            </Button>
          </Box>
        </Box>
        <Button>
          <EditIcon />
        </Button>
        <Button
          onClick={(event) => {
            event.stopPropagation();
            props.onDelete();
          }}
          sx={{marginLeft: 1}}
        >
          <DeleteForever color="error" />
        </Button>
      </Stack>
      {showTagDashboardUsage &&
        <TagDashboardsDialog
          onClose={() => setShowTagDashboardUsage(false)}
          onTagRemovedFromQuery={() => setDashboardCount(dashboardCount-1)}
          tag={props.tag}
        />
      }
      {showTagQueryUsage &&
        <TagQueriesDialog
          onClose={() => setShowTagQueryUsage(false)}
          onTagRemovedFromQuery={() => setQueryCount(queryCount-1)}
          tag={props.tag}
        />
      }
    </Paper>
  );
}